:host,
:root {
	/* Themed scrollbars, app-wide. The standard `scrollbar-color` and
	   `scrollbar-width` properties inherit, so declaring them on the theme
	   host propagates through light- and shadow-DOM descendants alike —
	   the calendar's month scroller, the drawer, the autocomplete listbox
	   all render the schmancy-tinted scrollbar without touching their
	   component CSS. Track is transparent so the surface beneath shows;
	   thumb is the outline-variant token so it tracks light/dark scheme. */
	scrollbar-width: thin;
	scrollbar-color: var(--schmancy-sys-color-outlineVariant) transparent;

	--md-ref-typeface-brand: var(--schmancy-font-family, sans-serif);
	--md-ref-typeface-plain: var(--schmancy-font-family, sans-serif);
	--md-sys-color-primary: var(--schmancy-sys-color-primary-default);
	--md-sys-color-secondary: var(--schmancy-sys-color-secondary-default);

	--default-font-family: var(--schmancy-font-family, ui-sans-serif, system-ui, sans-serif);
	--border-style: solid;
	--spacing: 4px;
	/* Eye-level anchor — perceptually constant top inset across a 13" laptop,
	   a 1080p monitor, and a 4K display. Use as a named spacing token so
	   sibling panels hang their first ink from one shared horizon
	   (`top-eye`, `pt-eye`, `mt-eye`, `h-eye`, …). */
	--spacing-eye: clamp(3rem, 6rem + 14vh, 13rem);
	--color-scrim: var(--schmancy-sys-color-scrim);
	--color-shadow: var(--schmancy-sys-color-shadow);
	--color-control-border: var(--schmancy-sys-control-border);
	--color-outline: var(--schmancy-sys-color-outline);
	--color-outlineVariant: var(--schmancy-sys-color-outlineVariant);
	--color-outline-variant: var(--schmancy-sys-color-outlineVariant);
	--color-surface-default: var(--schmancy-sys-color-surface-default);
	--color-surface-dim: var(--schmancy-sys-color-surface-dim);
	--color-surface-bright: var(--schmancy-sys-color-surface-bright);
	--color-surface-container: var(--schmancy-sys-color-surface-container);
	--color-surface-low: var(--schmancy-sys-color-surface-low);
	--color-surface-high: var(--schmancy-sys-color-surface-high);
	--color-surface-highest: var(--schmancy-sys-color-surface-highest);
	--color-surface-lowest: var(--schmancy-sys-color-surface-lowest);
	/* CamelCase variants for Tailwind compatibility */
	--color-surface-containerLow: var(--schmancy-sys-color-surface-containerLow);
	--color-surface-containerHigh: var(--schmancy-sys-color-surface-containerHigh);
	--color-surface-containerLowest: var(--schmancy-sys-color-surface-containerLowest);
	--color-surface-containerHighest: var(--schmancy-sys-color-surface-containerHighest);
	--color-surface-on: var(--schmancy-sys-color-surface-on);
	--color-surface-onVariant: var(--schmancy-sys-color-surface-onVariant);
	--color-surface-on-variant: var(--schmancy-sys-color-surface-onVariant);
	--color-surface-tint: var(--schmancy-sys-color-surface-tint);
	--color-surface-inverse: var(--schmancy-sys-color-surface-inverse);
	--color-surface-inverseOn: var(--schmancy-sys-color-surface-inverseOn);
	--color-inverse-surface: var(--schmancy-sys-color-surface-inverse);
	--color-inverse-on-surface: var(--schmancy-sys-color-surface-inverseOn);
	--color-primary-default: var(--schmancy-sys-color-primary-default);
	--color-primary-on: var(--schmancy-sys-color-primary-on);
	--color-primary-container: var(--schmancy-sys-color-primary-container);
	--color-primary-onContainer: var(--schmancy-sys-color-primary-onContainer);
	--color-primary-on-container: var(--schmancy-sys-color-primary-onContainer);
	--color-primary-fixed: var(--schmancy-sys-color-primary-fixed);
	--color-primary-fixedDim: var(--schmancy-sys-color-primary-fixedDim);
	--color-primary-fixed-dim: var(--schmancy-sys-color-primary-fixedDim);
	--color-primary-onFixed: var(--schmancy-sys-color-primary-onFixed);
	--color-primary-on-fixed: var(--schmancy-sys-color-primary-onFixed);
	--color-primary-onFixedVariant: var(--schmancy-sys-color-primary-onFixedVariant);
	--color-primary-on-fixed-variant: var(--schmancy-sys-color-primary-onFixedVariant);
	--color-primary-inverse: var(--schmancy-sys-color-primary-inverse);
	--color-inverse-primary: var(--schmancy-sys-color-primary-inverse);
	--color-secondary-default: var(--schmancy-sys-color-secondary-default);
	--color-secondary-on: var(--schmancy-sys-color-secondary-on);
	--color-secondary-container: var(--schmancy-sys-color-secondary-container);
	--color-secondary-onContainer: var(--schmancy-sys-color-secondary-onContainer);
	--color-secondary-on-container: var(--schmancy-sys-color-secondary-onContainer);
	--color-secondary-fixed: var(--schmancy-sys-color-secondary-fixed);
	--color-secondary-fixedDim: var(--schmancy-sys-color-secondary-fixedDim);
	--color-secondary-fixed-dim: var(--schmancy-sys-color-secondary-fixedDim);
	--color-secondary-onFixed: var(--schmancy-sys-color-secondary-onFixed);
	--color-secondary-on-fixed: var(--schmancy-sys-color-secondary-onFixed);
	--color-secondary-onFixedVariant: var(--schmancy-sys-color-secondary-onFixedVariant);
	--color-secondary-on-fixed-variant: var(--schmancy-sys-color-secondary-onFixedVariant);
	--color-tertiary-default: var(--schmancy-sys-color-tertiary-default);
	--color-tertiary-on: var(--schmancy-sys-color-tertiary-on);
	--color-tertiary-container: var(--schmancy-sys-color-tertiary-container);
	--color-tertiary-onContainer: var(--schmancy-sys-color-tertiary-onContainer);
	--color-tertiary-on-container: var(--schmancy-sys-color-tertiary-onContainer);
	--color-tertiary-fixed: var(--schmancy-sys-color-tertiary-fixed);
	--color-tertiary-fixedDim: var(--schmancy-sys-color-tertiary-fixedDim);
	--color-tertiary-fixed-dim: var(--schmancy-sys-color-tertiary-fixedDim);
	--color-tertiary-onFixed: var(--schmancy-sys-color-tertiary-onFixed);
	--color-tertiary-on-fixed: var(--schmancy-sys-color-tertiary-onFixed);
	--color-tertiary-onFixedVariant: var(--schmancy-sys-color-tertiary-onFixedVariant);
	--color-tertiary-on-fixed-variant: var(--schmancy-sys-color-tertiary-onFixedVariant);
	--color-error-default: var(--schmancy-sys-color-error-default);
	--color-error-on: var(--schmancy-sys-color-error-on);
	--color-error-container: var(--schmancy-sys-color-error-container);
	--color-error-onContainer: var(--schmancy-sys-color-error-onContainer);
	--color-error-on-container: var(--schmancy-sys-color-error-onContainer);
	--color-error-pinned: var(--schmancy-sys-color-error-pinned);
	--color-error-onPinned: var(--schmancy-sys-color-error-onPinned);
	--color-error-on-pinned: var(--schmancy-sys-color-error-onPinned);
	--color-success-default: var(--schmancy-sys-color-success-default);
	--color-success-on: var(--schmancy-sys-color-success-on);
	--color-success-container: var(--schmancy-sys-color-success-container);
	--color-success-onContainer: var(--schmancy-sys-color-success-onContainer);
	--color-success-on-container: var(--schmancy-sys-color-success-onContainer);
	--color-success-pinned: var(--schmancy-sys-color-success-pinned);
	--color-success-onPinned: var(--schmancy-sys-color-success-onPinned);
	--color-success-on-pinned: var(--schmancy-sys-color-success-onPinned);
	--color-warning-default: var(--schmancy-sys-color-warning-default);
	--color-warning-on: var(--schmancy-sys-color-warning-on);
	--color-warning-container: var(--schmancy-sys-color-warning-container);
	--color-warning-onContainer: var(--schmancy-sys-color-warning-onContainer);
	--color-warning-on-container: var(--schmancy-sys-color-warning-onContainer);
	--color-warning-pinned: var(--schmancy-sys-color-warning-pinned);
	--color-warning-onPinned: var(--schmancy-sys-color-warning-onPinned);
	--color-warning-on-pinned: var(--schmancy-sys-color-warning-onPinned);
	--color-info-default: var(--schmancy-sys-color-info-default);
	--color-info-on: var(--schmancy-sys-color-info-on);
	--color-info-container: var(--schmancy-sys-color-info-container);
	--color-info-onContainer: var(--schmancy-sys-color-info-onContainer);
	--color-info-on-container: var(--schmancy-sys-color-info-onContainer);
	--color-info-pinned: var(--schmancy-sys-color-info-pinned);
	--color-info-onPinned: var(--schmancy-sys-color-info-onPinned);
	--color-info-on-pinned: var(--schmancy-sys-color-info-onPinned);
	--shadow-xs: var(--schmancy-sys-elevation-0);
	--shadow-sm: var(--schmancy-sys-elevation-1);
	--shadow-md: var(--schmancy-sys-elevation-2);
	--shadow-lg: var(--schmancy-sys-elevation-3);
	--shadow-xl: var(--schmancy-sys-elevation-4);
	--shadow-2xl: var(--schmancy-sys-elevation-5);
	--outline-1: var(--schmancy-sys-outline-1);
	--font-sans:
		ui-sans-serif, system-ui, sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol', 'Noto Color Emoji';
	--font-serif: ui-serif, Georgia, Cambria, 'Times New Roman', Times, serif;
	--font-mono: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace;
	--color-red-50: oklch(0.971 0.013 17.38);
	--color-red-100: oklch(0.936 0.032 17.717);
	--color-red-200: oklch(0.885 0.062 18.334);
	--color-red-300: oklch(0.808 0.114 19.571);
	--color-red-400: oklch(0.704 0.191 22.216);
	--color-red-500: oklch(0.637 0.237 25.331);
	--color-red-600: oklch(0.577 0.245 27.325);
	--color-red-700: oklch(0.505 0.213 27.518);
	--color-red-800: oklch(0.444 0.177 26.899);
	--color-red-900: oklch(0.396 0.141 25.723);
	--color-red-950: oklch(0.258 0.092 26.042);
	--color-orange-50: oklch(0.98 0.016 73.684);
	--color-orange-100: oklch(0.954 0.038 75.164);
	--color-orange-200: oklch(0.901 0.076 70.697);
	--color-orange-300: oklch(0.837 0.128 66.29);
	--color-orange-400: oklch(0.75 0.183 55.934);
	--color-orange-500: oklch(0.705 0.213 47.604);
	--color-orange-600: oklch(0.646 0.222 41.116);
	--color-orange-700: oklch(0.553 0.195 38.402);
	--color-orange-800: oklch(0.47 0.157 37.304);
	--color-orange-900: oklch(0.408 0.123 38.172);
	--color-orange-950: oklch(0.266 0.079 36.259);
	--color-amber-50: oklch(0.987 0.022 95.277);
	--color-amber-100: oklch(0.962 0.059 95.617);
	--color-amber-200: oklch(0.924 0.12 95.746);
	--color-amber-300: oklch(0.879 0.169 91.605);
	--color-amber-400: oklch(0.828 0.189 84.429);
	--color-amber-500: oklch(0.769 0.188 70.08);
	--color-amber-600: oklch(0.666 0.179 58.318);
	--color-amber-700: oklch(0.555 0.163 48.998);
	--color-amber-800: oklch(0.473 0.137 46.201);
	--color-amber-900: oklch(0.414 0.112 45.904);
	--color-amber-950: oklch(0.279 0.077 45.635);
	--color-yellow-50: oklch(0.987 0.026 102.212);
	--color-yellow-100: oklch(0.973 0.071 103.193);
	--color-yellow-200: oklch(0.945 0.129 101.54);
	--color-yellow-300: oklch(0.905 0.182 98.111);
	--color-yellow-400: oklch(0.852 0.199 91.936);
	--color-yellow-500: oklch(0.795 0.184 86.047);
	--color-yellow-600: oklch(0.681 0.162 75.834);
	--color-yellow-700: oklch(0.554 0.135 66.442);
	--color-yellow-800: oklch(0.476 0.114 61.907);
	--color-yellow-900: oklch(0.421 0.095 57.708);
	--color-yellow-950: oklch(0.286 0.066 53.813);
	--color-lime-50: oklch(0.986 0.031 120.757);
	--color-lime-100: oklch(0.967 0.067 122.328);
	--color-lime-200: oklch(0.938 0.127 124.321);
	--color-lime-300: oklch(0.897 0.196 126.665);
	--color-lime-400: oklch(0.841 0.238 128.85);
	--color-lime-500: oklch(0.768 0.233 130.85);
	--color-lime-600: oklch(0.648 0.2 131.684);
	--color-lime-700: oklch(0.532 0.157 131.589);
	--color-lime-800: oklch(0.453 0.124 130.933);
	--color-lime-900: oklch(0.405 0.101 131.063);
	--color-lime-950: oklch(0.274 0.072 132.109);
	--color-green-50: oklch(0.982 0.018 155.826);
	--color-green-100: oklch(0.962 0.044 156.743);
	--color-green-200: oklch(0.925 0.084 155.995);
	--color-green-300: oklch(0.871 0.15 154.449);
	--color-green-400: oklch(0.792 0.209 151.711);
	--color-green-500: oklch(0.723 0.219 149.579);
	--color-green-600: oklch(0.627 0.194 149.214);
	--color-green-700: oklch(0.527 0.154 150.069);
	--color-green-800: oklch(0.448 0.119 151.328);
	--color-green-900: oklch(0.393 0.095 152.535);
	--color-green-950: oklch(0.266 0.065 152.934);
	--color-emerald-50: oklch(0.979 0.021 166.113);
	--color-emerald-100: oklch(0.95 0.052 163.051);
	--color-emerald-200: oklch(0.905 0.093 164.15);
	--color-emerald-300: oklch(0.845 0.143 164.978);
	--color-emerald-400: oklch(0.765 0.177 163.223);
	--color-emerald-500: oklch(0.696 0.17 162.48);
	--color-emerald-600: oklch(0.596 0.145 163.225);
	--color-emerald-700: oklch(0.508 0.118 165.612);
	--color-emerald-800: oklch(0.432 0.095 166.913);
	--color-emerald-900: oklch(0.378 0.077 168.94);
	--color-emerald-950: oklch(0.262 0.051 172.552);
	--color-teal-50: oklch(0.984 0.014 180.72);
	--color-teal-100: oklch(0.953 0.051 180.801);
	--color-teal-200: oklch(0.91 0.096 180.426);
	--color-teal-300: oklch(0.855 0.138 181.071);
	--color-teal-400: oklch(0.777 0.152 181.912);
	--color-teal-500: oklch(0.704 0.14 182.503);
	--color-teal-600: oklch(0.6 0.118 184.704);
	--color-teal-700: oklch(0.511 0.096 186.391);
	--color-teal-800: oklch(0.437 0.078 188.216);
	--color-teal-900: oklch(0.386 0.063 188.416);
	--color-teal-950: oklch(0.277 0.046 192.524);
	--color-cyan-50: oklch(0.984 0.019 200.873);
	--color-cyan-100: oklch(0.956 0.045 203.388);
	--color-cyan-200: oklch(0.917 0.08 205.041);
	--color-cyan-300: oklch(0.865 0.127 207.078);
	--color-cyan-400: oklch(0.789 0.154 211.53);
	--color-cyan-500: oklch(0.715 0.143 215.221);
	--color-cyan-600: oklch(0.609 0.126 221.723);
	--color-cyan-700: oklch(0.52 0.105 223.128);
	--color-cyan-800: oklch(0.45 0.085 224.283);
	--color-cyan-900: oklch(0.398 0.07 227.392);
	--color-cyan-950: oklch(0.302 0.056 229.695);
	--color-sky-50: oklch(0.977 0.013 236.62);
	--color-sky-100: oklch(0.951 0.026 236.824);
	--color-sky-200: oklch(0.901 0.058 230.902);
	--color-sky-300: oklch(0.828 0.111 230.318);
	--color-sky-400: oklch(0.746 0.16 232.661);
	--color-sky-500: oklch(0.685 0.169 237.323);
	--color-sky-600: oklch(0.588 0.158 241.966);
	--color-sky-700: oklch(0.5 0.134 242.749);
	--color-sky-800: oklch(0.443 0.11 240.79);
	--color-sky-900: oklch(0.391 0.09 240.876);
	--color-sky-950: oklch(0.293 0.066 243.157);
	--color-blue-50: oklch(0.97 0.014 254.604);
	--color-blue-100: oklch(0.932 0.032 255.585);
	--color-blue-200: oklch(0.882 0.059 254.128);
	--color-blue-300: oklch(0.809 0.105 251.813);
	--color-blue-400: oklch(0.707 0.165 254.624);
	--color-blue-500: oklch(0.623 0.214 259.815);
	--color-blue-600: oklch(0.546 0.245 262.881);
	--color-blue-700: oklch(0.488 0.243 264.376);
	--color-blue-800: oklch(0.424 0.199 265.638);
	--color-blue-900: oklch(0.379 0.146 265.522);
	--color-blue-950: oklch(0.282 0.091 267.935);
	--color-indigo-50: oklch(0.962 0.018 272.314);
	--color-indigo-100: oklch(0.93 0.034 272.788);
	--color-indigo-200: oklch(0.87 0.065 274.039);
	--color-indigo-300: oklch(0.785 0.115 274.713);
	--color-indigo-400: oklch(0.673 0.182 276.935);
	--color-indigo-500: oklch(0.585 0.233 277.117);
	--color-indigo-600: oklch(0.511 0.262 276.966);
	--color-indigo-700: oklch(0.457 0.24 277.023);
	--color-indigo-800: oklch(0.398 0.195 277.366);
	--color-indigo-900: oklch(0.359 0.144 278.697);
	--color-indigo-950: oklch(0.257 0.09 281.288);
	--color-violet-50: oklch(0.969 0.016 293.756);
	--color-violet-100: oklch(0.943 0.029 294.588);
	--color-violet-200: oklch(0.894 0.057 293.283);
	--color-violet-300: oklch(0.811 0.111 293.571);
	--color-violet-400: oklch(0.702 0.183 293.541);
	--color-violet-500: oklch(0.606 0.25 292.717);
	--color-violet-600: oklch(0.541 0.281 293.009);
	--color-violet-700: oklch(0.491 0.27 292.581);
	--color-violet-800: oklch(0.432 0.232 292.759);
	--color-violet-900: oklch(0.38 0.189 293.745);
	--color-violet-950: oklch(0.283 0.141 291.089);
	--color-purple-50: oklch(0.977 0.014 308.299);
	--color-purple-100: oklch(0.946 0.033 307.174);
	--color-purple-200: oklch(0.902 0.063 306.703);
	--color-purple-300: oklch(0.827 0.119 306.383);
	--color-purple-400: oklch(0.714 0.203 305.504);
	--color-purple-500: oklch(0.627 0.265 303.9);
	--color-purple-600: oklch(0.558 0.288 302.321);
	--color-purple-700: oklch(0.496 0.265 301.924);
	--color-purple-800: oklch(0.438 0.218 303.724);
	--color-purple-900: oklch(0.381 0.176 304.987);
	--color-purple-950: oklch(0.291 0.149 302.717);
	--color-fuchsia-50: oklch(0.977 0.017 320.058);
	--color-fuchsia-100: oklch(0.952 0.037 318.852);
	--color-fuchsia-200: oklch(0.903 0.076 319.62);
	--color-fuchsia-300: oklch(0.833 0.145 321.434);
	--color-fuchsia-400: oklch(0.74 0.238 322.16);
	--color-fuchsia-500: oklch(0.667 0.295 322.15);
	--color-fuchsia-600: oklch(0.591 0.293 322.896);
	--color-fuchsia-700: oklch(0.518 0.253 323.949);
	--color-fuchsia-800: oklch(0.452 0.211 324.591);
	--color-fuchsia-900: oklch(0.401 0.17 325.612);
	--color-fuchsia-950: oklch(0.293 0.136 325.661);
	--color-pink-50: oklch(0.971 0.014 343.198);
	--color-pink-100: oklch(0.948 0.028 342.258);
	--color-pink-200: oklch(0.899 0.061 343.231);
	--color-pink-300: oklch(0.823 0.12 346.018);
	--color-pink-400: oklch(0.718 0.202 349.761);
	--color-pink-500: oklch(0.656 0.241 354.308);
	--color-pink-600: oklch(0.592 0.249 0.584);
	--color-pink-700: oklch(0.525 0.223 3.958);
	--color-pink-800: oklch(0.459 0.187 3.815);
	--color-pink-900: oklch(0.408 0.153 2.432);
	--color-pink-950: oklch(0.284 0.109 3.907);
	--color-rose-50: oklch(0.969 0.015 12.422);
	--color-rose-100: oklch(0.941 0.03 12.58);
	--color-rose-200: oklch(0.892 0.058 10.001);
	--color-rose-300: oklch(0.81 0.117 11.638);
	--color-rose-400: oklch(0.712 0.194 13.428);
	--color-rose-500: oklch(0.645 0.246 16.439);
	--color-rose-600: oklch(0.586 0.253 17.585);
	--color-rose-700: oklch(0.514 0.222 16.935);
	--color-rose-800: oklch(0.455 0.188 13.697);
	--color-rose-900: oklch(0.41 0.159 10.272);
	--color-rose-950: oklch(0.271 0.105 12.094);
	--color-slate-50: oklch(0.984 0.003 247.858);
	--color-slate-100: oklch(0.968 0.007 247.896);
	--color-slate-200: oklch(0.929 0.013 255.508);
	--color-slate-300: oklch(0.869 0.022 252.894);
	--color-slate-400: oklch(0.704 0.04 256.788);
	--color-slate-500: oklch(0.554 0.046 257.417);
	--color-slate-600: oklch(0.446 0.043 257.281);
	--color-slate-700: oklch(0.372 0.044 257.287);
	--color-slate-800: oklch(0.279 0.041 260.031);
	--color-slate-900: oklch(0.208 0.042 265.755);
	--color-slate-950: oklch(0.129 0.042 264.695);
	--color-gray-50: oklch(0.985 0.002 247.839);
	--color-gray-100: oklch(0.967 0.003 264.542);
	--color-gray-200: oklch(0.928 0.006 264.531);
	--color-gray-300: oklch(0.872 0.01 258.338);
	--color-gray-400: oklch(0.707 0.022 261.325);
	--color-gray-500: oklch(0.551 0.027 264.364);
	--color-gray-600: oklch(0.446 0.03 256.802);
	--color-gray-700: oklch(0.373 0.034 259.733);
	--color-gray-800: oklch(0.278 0.033 256.848);
	--color-gray-900: oklch(0.21 0.034 264.665);
	--color-gray-950: oklch(0.13 0.028 261.692);
	--color-zinc-50: oklch(0.985 0 0);
	--color-zinc-100: oklch(0.967 0.001 286.375);
	--color-zinc-200: oklch(0.92 0.004 286.32);
	--color-zinc-300: oklch(0.871 0.006 286.286);
	--color-zinc-400: oklch(0.705 0.015 286.067);
	--color-zinc-500: oklch(0.552 0.016 285.938);
	--color-zinc-600: oklch(0.442 0.017 285.786);
	--color-zinc-700: oklch(0.37 0.013 285.805);
	--color-zinc-800: oklch(0.274 0.006 286.033);
	--color-zinc-900: oklch(0.21 0.006 285.885);
	--color-zinc-950: oklch(0.141 0.005 285.823);
	--color-neutral-50: oklch(0.985 0 0);
	--color-neutral-100: oklch(0.97 0 0);
	--color-neutral-200: oklch(0.922 0 0);
	--color-neutral-300: oklch(0.87 0 0);
	--color-neutral-400: oklch(0.708 0 0);
	--color-neutral-500: oklch(0.556 0 0);
	--color-neutral-600: oklch(0.439 0 0);
	--color-neutral-700: oklch(0.371 0 0);
	--color-neutral-800: oklch(0.269 0 0);
	--color-neutral-900: oklch(0.205 0 0);
	--color-neutral-950: oklch(0.145 0 0);
	--color-stone-50: oklch(0.985 0.001 106.423);
	--color-stone-100: oklch(0.97 0.001 106.424);
	--color-stone-200: oklch(0.923 0.003 48.717);
	--color-stone-300: oklch(0.869 0.005 56.366);
	--color-stone-400: oklch(0.709 0.01 56.259);
	--color-stone-500: oklch(0.553 0.013 58.071);
	--color-stone-600: oklch(0.444 0.011 73.639);
	--color-stone-700: oklch(0.374 0.01 67.558);
	--color-stone-800: oklch(0.268 0.007 34.298);
	--color-stone-900: oklch(0.216 0.006 56.043);
	--color-stone-950: oklch(0.147 0.004 49.25);
	--color-black: #000;
	--color-white: #fff;
	--spacing: 0.25rem;
	--breakpoint-sm: 40rem;
	--breakpoint-md: 48rem;
	--breakpoint-lg: 64rem;
	--breakpoint-xl: 80rem;
	--breakpoint-2xl: 96rem;
	--container-3xs: 16rem;
	--container-2xs: 18rem;
	--container-xs: 20rem;
	--container-sm: 24rem;
	--container-md: 28rem;
	--container-lg: 32rem;
	--container-xl: 36rem;
	--container-2xl: 42rem;
	--container-3xl: 48rem;
	--container-4xl: 56rem;
	--container-5xl: 64rem;
	--container-6xl: 72rem;
	--container-7xl: 80rem;
	--text-xs: 0.75rem;
	--text-xs--line-height: calc(1 / 0.75);
	--text-sm: 0.875rem;
	--text-sm--line-height: calc(1.25 / 0.875);
	--text-base: 1rem;
	--text-base--line-height: calc(1.5 / 1);
	--text-lg: 1.125rem;
	--text-lg--line-height: calc(1.75 / 1.125);
	--text-xl: 1.25rem;
	--text-xl--line-height: calc(1.75 / 1.25);
	--text-2xl: 1.5rem;
	--text-2xl--line-height: calc(2 / 1.5);
	--text-3xl: 1.875rem;
	--text-3xl--line-height: calc(2.25 / 1.875);
	--text-4xl: 2.25rem;
	--text-4xl--line-height: calc(2.5 / 2.25);
	--text-5xl: 3rem;
	--text-5xl--line-height: 1;
	--text-6xl: 3.75rem;
	--text-6xl--line-height: 1;
	--text-7xl: 4.5rem;
	--text-7xl--line-height: 1;
	--text-8xl: 6rem;
	--text-8xl--line-height: 1;
	--text-9xl: 8rem;
	--text-9xl--line-height: 1;
	--font-weight-thin: 100;
	--font-weight-extralight: 200;
	--font-weight-light: 300;
	--font-weight-normal: 400;
	--font-weight-medium: 500;
	--font-weight-semibold: 600;
	--font-weight-bold: 700;
	--font-weight-extrabold: 800;
	--font-weight-black: 900;
	--tracking-tighter: -0.05em;
	--tracking-tight: -0.025em;
	--tracking-normal: 0em;
	--tracking-wide: 0.025em;
	--tracking-wider: 0.05em;
	--tracking-widest: 0.1em;
	--tracking-eyebrow: 0.18em;
	--tracking-label: 0.14em;
	--leading-display: 0.95;
	--leading-tight: 1.25;
	--leading-snug: 1.375;
	--leading-normal: 1.5;
	--leading-relaxed: 1.625;
	--leading-loose: 2;
	/* The type scale <schmancy-typography> renders: six roles × five rungs,
	   each a --text-* Tailwind namespace token resolving through the
	   --schmancy-sys-typescale-* channel, so a rebrand rescales a rung by
	   declaring one token instead of editing the component. Each carries the
	   literal it defaults to — the channel is unset until an app declares it,
	   and an unset var() with no fallback is invalid-at-computed-value-time.
	   Mirrors the block in packages/mixins/src/tailwind.css's `@theme inline`
	   — keep the two in sync. typography.ts's 30 size rules read the same
	   channel directly, the way a corner reads --schmancy-radius-*. */
	--text-display-xl: var(--schmancy-sys-typescale-display-xl-size, 72px);
	--text-display-xl--line-height: var(--schmancy-sys-typescale-display-xl-line-height, 80px);
	--text-display-lg: var(--schmancy-sys-typescale-display-lg-size, 57px);
	--text-display-lg--line-height: var(--schmancy-sys-typescale-display-lg-line-height, 64px);
	--text-display-md: var(--schmancy-sys-typescale-display-md-size, 45px);
	--text-display-md--line-height: var(--schmancy-sys-typescale-display-md-line-height, 52px);
	--text-display-sm: var(--schmancy-sys-typescale-display-sm-size, 36px);
	--text-display-sm--line-height: var(--schmancy-sys-typescale-display-sm-line-height, 44px);
	--text-display-xs: var(--schmancy-sys-typescale-display-xs-size, 28px);
	--text-display-xs--line-height: var(--schmancy-sys-typescale-display-xs-line-height, 36px);
	--text-headline-xl: var(--schmancy-sys-typescale-headline-xl-size, 36px);
	--text-headline-xl--line-height: var(--schmancy-sys-typescale-headline-xl-line-height, 44px);
	--text-headline-lg: var(--schmancy-sys-typescale-headline-lg-size, 32px);
	--text-headline-lg--line-height: var(--schmancy-sys-typescale-headline-lg-line-height, 40px);
	--text-headline-md: var(--schmancy-sys-typescale-headline-md-size, 28px);
	--text-headline-md--line-height: var(--schmancy-sys-typescale-headline-md-line-height, 36px);
	--text-headline-sm: var(--schmancy-sys-typescale-headline-sm-size, 24px);
	--text-headline-sm--line-height: var(--schmancy-sys-typescale-headline-sm-line-height, 32px);
	--text-headline-xs: var(--schmancy-sys-typescale-headline-xs-size, 20px);
	--text-headline-xs--line-height: var(--schmancy-sys-typescale-headline-xs-line-height, 28px);
	--text-title-xl: var(--schmancy-sys-typescale-title-xl-size, 24px);
	--text-title-xl--line-height: var(--schmancy-sys-typescale-title-xl-line-height, 32px);
	--text-title-lg: var(--schmancy-sys-typescale-title-lg-size, 22px);
	--text-title-lg--line-height: var(--schmancy-sys-typescale-title-lg-line-height, 28px);
	--text-title-md: var(--schmancy-sys-typescale-title-md-size, 16px);
	--text-title-md--line-height: var(--schmancy-sys-typescale-title-md-line-height, 24px);
	--text-title-sm: var(--schmancy-sys-typescale-title-sm-size, 14px);
	--text-title-sm--line-height: var(--schmancy-sys-typescale-title-sm-line-height, 20px);
	--text-title-xs: var(--schmancy-sys-typescale-title-xs-size, 12px);
	--text-title-xs--line-height: var(--schmancy-sys-typescale-title-xs-line-height, 16px);
	--text-subtitle-xl: var(--schmancy-sys-typescale-subtitle-xl-size, 20px);
	--text-subtitle-xl--line-height: var(--schmancy-sys-typescale-subtitle-xl-line-height, 28px);
	--text-subtitle-lg: var(--schmancy-sys-typescale-subtitle-lg-size, 18px);
	--text-subtitle-lg--line-height: var(--schmancy-sys-typescale-subtitle-lg-line-height, 24px);
	--text-subtitle-md: var(--schmancy-sys-typescale-subtitle-md-size, 16px);
	--text-subtitle-md--line-height: var(--schmancy-sys-typescale-subtitle-md-line-height, 24px);
	--text-subtitle-sm: var(--schmancy-sys-typescale-subtitle-sm-size, 14px);
	--text-subtitle-sm--line-height: var(--schmancy-sys-typescale-subtitle-sm-line-height, 20px);
	--text-subtitle-xs: var(--schmancy-sys-typescale-subtitle-xs-size, 12px);
	--text-subtitle-xs--line-height: var(--schmancy-sys-typescale-subtitle-xs-line-height, 16px);
	--text-body-xl: var(--schmancy-sys-typescale-body-xl-size, 18px);
	--text-body-xl--line-height: var(--schmancy-sys-typescale-body-xl-line-height, 28px);
	--text-body-lg: var(--schmancy-sys-typescale-body-lg-size, 16px);
	--text-body-lg--line-height: var(--schmancy-sys-typescale-body-lg-line-height, 24px);
	--text-body-md: var(--schmancy-sys-typescale-body-md-size, 14px);
	--text-body-md--line-height: var(--schmancy-sys-typescale-body-md-line-height, 20px);
	--text-body-sm: var(--schmancy-sys-typescale-body-sm-size, 12px);
	--text-body-sm--line-height: var(--schmancy-sys-typescale-body-sm-line-height, 16px);
	--text-body-xs: var(--schmancy-sys-typescale-body-xs-size, 10px);
	--text-body-xs--line-height: var(--schmancy-sys-typescale-body-xs-line-height, 14px);
	--text-label-xl: var(--schmancy-sys-typescale-label-xl-size, 16px);
	--text-label-xl--line-height: var(--schmancy-sys-typescale-label-xl-line-height, 22px);
	--text-label-lg: var(--schmancy-sys-typescale-label-lg-size, 14px);
	--text-label-lg--line-height: var(--schmancy-sys-typescale-label-lg-line-height, 20px);
	--text-label-md: var(--schmancy-sys-typescale-label-md-size, 12px);
	--text-label-md--line-height: var(--schmancy-sys-typescale-label-md-line-height, 16px);
	--text-label-sm: var(--schmancy-sys-typescale-label-sm-size, 11px);
	--text-label-sm--line-height: var(--schmancy-sys-typescale-label-sm-line-height, 16px);
	--text-label-xs: var(--schmancy-sys-typescale-label-xs-size, 10px);
	--text-label-xs--line-height: var(--schmancy-sys-typescale-label-xs-line-height, 14px);
	/* This stylesheet is imported ?inline into <schmancy-theme>'s static
	   styles, so its :root selector here never matches — only :host, which
	   matches the <schmancy-theme> element itself. Backoffice and designer
	   both wrap their whole shell in <schmancy-theme root>, making this the
	   nearest declarer of --radius-* for every descendant: it beats the
	   document :root by inheritance proximity and is what actually sets
	   light-DOM radii in those apps today. Mirrors the ladder in
	   packages/mixins/src/tailwind.css's `@theme inline` block — keep the
	   two in sync. sm/md/4xl are deleted — every call site moved to a
	   ladder rung. */
	--radius-xs: var(--schmancy-radius-xs, 0.4375rem); /* 7px */
	--radius-lg: var(--schmancy-radius-lg, 0.625rem); /* 10px */
	--radius-xl: var(--schmancy-radius-xl, 0.875rem); /* 14px */
	--radius-2xl: var(--schmancy-radius-2xl, 1.25rem); /* 20px */
	--radius-3xl: var(--schmancy-radius-3xl, 1.75rem); /* 28px */
	--radius-bezel: var(--schmancy-radius-bezel, 2.5rem); /* 40px — the anchor */
	/* Geometry, not ratio — setBase() never writes these; only setShape() does. */
	--radius-full: var(--schmancy-radius-full, calc(infinity * 1px));
	--radius-circle: var(--schmancy-radius-circle, 50%);
	--shadow-smxs: 0 1px rgb(0 0 0 / 0.05);
	--shadow-xs: 0 1px 2px 0 rgb(0 0 0 / 0.05);
	--shadow-sm: 0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1);
	--shadow-md: 0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1);
	--shadow-lg: 0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1);
	--shadow-xl: 0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1);
	--shadow-smxl: 0 25px 50px -12px rgb(0 0 0 / 0.25);
	--inset-shadow-smxs: inset 0 1px rgb(0 0 0 / 0.05);
	--inset-shadow-xs: inset 0 1px 1px rgb(0 0 0 / 0.05);
	--inset-shadow-sm: inset 0 2px 4px rgb(0 0 0 / 0.05);
	--drop-shadow-xs: 0 1px 1px rgb(0 0 0 / 0.05);
	--drop-shadow-sm: 0 1px 2px rgb(0 0 0 / 0.15);
	--drop-shadow-md: 0 3px 3px rgb(0 0 0 / 0.12);
	--drop-shadow-lg: 0 4px 4px rgb(0 0 0 / 0.15);
	--drop-shadow-xl: 0 9px 7px rgb(0 0 0 / 0.1);
	--drop-shadow-smxl: 0 25px 25px rgb(0 0 0 / 0.15);
	--blur-xs: 4px;
	--blur-sm: 8px;
	--blur-md: 12px;
	--blur-lg: 16px;
	--blur-xl: 24px;
	--blur-2xl: 40px;
	--blur-3xl: 64px;
	--perspective-dramatic: 100px;
	--perspective-near: 300px;
	--perspective-normal: 500px;
	--perspective-midrange: 800px;
	--perspective-distant: 1200px;
	--aspect-video: 16 / 9;
	--aspect-a4: 210 / 297;
	--ease-in: cubic-bezier(0.4, 0, 1, 1);
	--ease-out: cubic-bezier(0, 0, 0.2, 1);
	--ease-in-out: cubic-bezier(0.4, 0, 0.2, 1);
	--animate-spin: spin 1s linear infinite;
	--animate-ping: ping 1s cubic-bezier(0, 0, 0.2, 1) infinite;
	--animate-pulse: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
	--animate-bounce: bounce 1s infinite;
	/* Presentation animations */
	--animate-float-1: float-drift 20s ease-in-out infinite;
	--animate-float-2: float-drift-alt 25s ease-in-out infinite;
	--animate-float-3: float-drift 30s ease-in-out infinite reverse;
	--animate-pulse-glow: pulse-glow 8s ease-in-out infinite;
	--animate-spin-slow: slow-spin 120s linear infinite;

	/* Scene nebula layers */
	--animate-aurora-drift: aurora-drift 30s ease-in-out infinite;
	--animate-scene-enter: scene-enter 1.2s cubic-bezier(0.16, 1, 0.3, 1) forwards;

	/* Color System */
	--md-sys-color-scrim: var(--schmancy-sys-color-scrim);
	--md-sys-color-outline: var(--schmancy-sys-color-outline);
	--md-sys-color-outlineVariant: var(--schmancy-sys-color-outlineVariant);
	/* Surface Colors */
	--md-sys-color-surface-default: var(--schmancy-sys-color-surface-default);
	--md-sys-color-surface-dim: var(--schmancy-sys-color-surface-dim);
	--md-sys-color-surface-bright: var(--schmancy-sys-color-surface-bright);
	--md-sys-color-surface-container: var(--schmancy-sys-color-surface-container);
	--md-sys-color-surface-low: var(--schmancy-sys-color-surface-low);
	--md-sys-color-surface-high: var(--schmancy-sys-color-surface-high);
	--md-sys-color-surface-highest: var(--schmancy-sys-color-surface-highest);
	--md-sys-color-surface-lowest: var(--schmancy-sys-color-surface-lowest);
	--md-sys-color-surface-on: var(--schmancy-sys-color-surface-on);
	--md-sys-color-on-surface-variant: var(--schmancy-sys-color-surface-onVariant);
	--md-sys-color-on-secondary-container: var(--schmancy-sys-color-secondary-onContainer);
	/* Primary Colors */
	--md-sys-color-primary-default: var(--schmancy-sys-color-primary-default);
	--md-sys-color-primary-on: var(--schmancy-sys-color-primary-on);
	--md-sys-color-primary-container: var(--schmancy-sys-color-primary-container);
	--md-sys-color-primary-onContainer: var(--schmancy-sys-color-primary-onContainer);

	/* Secondary Colors */
	--md-sys-color-secondary-default: var(--schmancy-sys-color-secondary-default);
	--md-sys-color-secondary-on: var(--schmancy-sys-color-secondary-on);
	--md-sys-color-secondary-container: var(--schmancy-sys-color-secondary-container);
	--md-sys-color-secondary-onContainer: var(--schmancy-sys-color-secondary-onContainer);

	/* Tertiary Colors */
	--md-sys-color-tertiary-default: var(--schmancy-sys-color-tertiary-default);
	--md-sys-color-tertiary-on: var(--schmancy-sys-color-tertiary-on);
	--md-sys-color-tertiary-container: var(--schmancy-sys-color-tertiary-container);
	--md-sys-color-tertiary-onContainer: var(--schmancy-sys-color-tertiary-onContainer);

	/* Error Colors */
	--md-sys-color-error-default: var(--schmancy-sys-color-error-default);
	--md-sys-color-error-on: var(--schmancy-sys-color-error-on);
	--md-sys-color-error-container: var(--schmancy-sys-color-error-container);
	--md-sys-color-error-onContainer: var(--schmancy-sys-color-error-onContainer);

	/* Success Colors */
	--md-sys-color-success-default: var(--schmancy-sys-color-success-default);
	--md-sys-color-success-on: var(--schmancy-sys-color-success-on);
	--md-sys-color-success-container: var(--schmancy-sys-color-success-container);
	--md-sys-color-success-onContainer: var(--schmancy-sys-color-success-onContainer);

	/* Warning Colors */
	--md-sys-color-warning-default: var(--schmancy-sys-color-warning-default);
	--md-sys-color-warning-on: var(--schmancy-sys-color-warning-on);
	--md-sys-color-warning-container: var(--schmancy-sys-color-warning-container);
	--md-sys-color-warning-onContainer: var(--schmancy-sys-color-warning-onContainer);

	/* Info Colors */
	--md-sys-color-info-default: var(--schmancy-sys-color-info-default);
	--md-sys-color-info-on: var(--schmancy-sys-color-info-on);
	--md-sys-color-info-container: var(--schmancy-sys-color-info-container);
	--md-sys-color-info-onContainer: var(--schmancy-sys-color-info-onContainer);

	/* Elevation Levels */
	--md-sys-elevation-0: var(--schmancy-sys-elevation-0);
	--md-sys-elevation-1: var(--schmancy-sys-elevation-1);
	--md-sys-elevation-2: var(--schmancy-sys-elevation-2);
	--md-sys-elevation-3: var(--schmancy-sys-elevation-3);
	--md-sys-elevation-4: var(--schmancy-sys-elevation-4);
	--md-sys-elevation-5: var(--schmancy-sys-elevation-5);

	/* Outline */
	--md-sys-outline-1: var(--schmancy-sys-outline-1);

	/* Chips */
	--md-filter-chip-label-text-color: var(--schmancy-sys-color-surface-on);

	/* Checkbox */
	--md-checkbox-focus-outline-color: var(--schmancy-sys-color-surface-on);
	--md-checkbox-focus-outline-width: 2px;
	--md-checkbox-hover-outline-color: var(--schmancy-sys-color-surface-on);
	--md-checkbox-hover-outline-width: 2px;
	--md-checkbox-pressed-outline-color: var(--schmancy-sys-color-surface-on);
	--md-checkbox-pressed-outline-width: 2px;
	--md-checkbox-disabled-outline-color: var(--schmancy-sys-color-surface-on);
	--md-checkbox-disabled-outline-width: 2px;
}

@keyframes spin {
	to {
		transform: rotate(360deg);
	}
}
@keyframes ping {
	75%,
	100% {
		transform: scale(2);
		opacity: 0;
	}
}
@keyframes pulse {
	50% {
		opacity: 0.5;
	}
}
@keyframes bounce {
	0%,
	100% {
		transform: translateY(-25%);
		animation-timing-function: cubic-bezier(0.8, 0, 1, 1);
	}
	50% {
		transform: none;
		animation-timing-function: cubic-bezier(0, 0, 0.2, 1);
	}
}
@keyframes float-drift {
	0%,
	100% {
		transform: translate(0, 0) rotate(0deg);
	}
	25% {
		transform: translate(20px, -30px) rotate(2deg);
	}
	50% {
		transform: translate(-10px, -50px) rotate(-1deg);
	}
	75% {
		transform: translate(15px, -20px) rotate(1deg);
	}
}
@keyframes float-drift-alt {
	0%,
	100% {
		transform: translate(0, 0) rotate(0deg);
	}
	25% {
		transform: translate(-25px, 20px) rotate(-2deg);
	}
	50% {
		transform: translate(15px, 40px) rotate(1deg);
	}
	75% {
		transform: translate(-20px, 15px) rotate(-1deg);
	}
}
@keyframes pulse-glow {
	0%,
	100% {
		opacity: 0.15;
		transform: scale(1);
	}
	50% {
		opacity: 0.25;
		transform: scale(1.05);
	}
}
@keyframes slow-spin {
	from {
		transform: rotate(0deg);
	}
	to {
		transform: rotate(360deg);
	}
}
@keyframes aurora-drift {
	0%,
	100% {
		transform: translate(0, 0) scale(1) rotate(0deg);
	}
	33% {
		transform: translate(3%, -5%) scale(1.05) rotate(1deg);
	}
	66% {
		transform: translate(-3%, 3%) scale(0.95) rotate(-1deg);
	}
}
@keyframes scene-enter {
	from {
		opacity: 0;
		transform: scale(0.92);
		filter: blur(12px);
	}
	to {
		opacity: 1;
		transform: scale(1);
		filter: blur(0px);
	}
}
@keyframes star-appear {
	from {
		opacity: 0;
		transform: scale(0.5);
	}
	to {
		opacity: 1;
		transform: scale(1);
	}
}
@keyframes star-twinkle {
	0%,
	100% {
		opacity: 1;
		transform: scale(1);
	}
	50% {
		opacity: 0.4;
		transform: scale(0.92);
	}
}
